import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import React, { FC, ReactNode, useId, useRef, useState } from 'react';
import { Control } from 'react-hook-form';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon, SearchIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import {
  ComboboxButton,
  ComboboxOptions,
  FormCombobox,
  FormComboboxInput,
  FormComboboxOption,
} from '../../../../components/combobox/index';
import Loader from '../../../../components/loader/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useDebounce } from '../../../../hooks/index';
import { useStakeholdersWithShares } from '../../../../hooks/stakeholder/index';
import { useInfiniteStakeholders } from '../../../../hooks/stakeholder/use-infinite-stakeholders';
import {
  Stakeholder,
  StakeholderOrder,
  stakeholderSingularTypeTitle,
  StakeholderType,
} from '../../../../types/stakeholder.types';
import { FormSchema } from '../event-form.validation';

export type StakeholderValue = {
  email?: string;
  fullName: string;
  id: string;
  isTerminated?: boolean;
};

const DEFAULT_ADD_BUTTON_LABEL = (
  <>
    + <AppFormattedMessage id="ADD_NEW_SHAREHOLDER" />
  </>
);

export type StakeholdersItemsListProps = {
  addButtonLabel?: ReactNode;
  control: Control<FormSchema>;
  data: Stakeholder[];
  emptyListMessage?: ReactNode;
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<
        {
          stakeholders: Stakeholder[];
          totalPages: number;
        },
        unknown
      >,
      Error
    >
  >;
  hasNextPage: boolean;
  hasSearchValue: boolean;
  isLoading: boolean;
  name: string;
  scrollableTargetId: string;
};

const StakeholdersItemsList: FC<StakeholdersItemsListProps> = ({
  addButtonLabel = DEFAULT_ADD_BUTTON_LABEL,
  control,
  data,
  emptyListMessage,
  fetchNextPage,
  hasNextPage,
  hasSearchValue,
  isLoading,
  name,
  scrollableTargetId,
}) => {
  const navigate = useNavigate();
  return (
    <div className="flex w-full flex-col gap-2">
      <InfiniteScroll
        className="flex w-full flex-col gap-2"
        dataLength={data.length || 0}
        hasMore={hasNextPage}
        loader={<Loader iconClassName="size-8" />}
        next={fetchNextPage}
        scrollableTarget={scrollableTargetId}
        style={{ overflow: 'visible' }}
      >
        {data.length > 0 ? (
          <>
            {data.map(({ email, fullName, id, isTerminated, type }, i) => (
              <FormComboboxOption
                className="w-full"
                control={control}
                key={`${fullName}_${i}`}
                name={name as keyof FormSchema}
                value={
                  {
                    id,
                    fullName,
                    // Rides along so a form can react to the choice without re-fetching the
                    // stakeholder — reverse vesting freezes itself for a terminated one.
                    isTerminated,
                    ...(email != null && email !== '' && { email }),
                  } as unknown as FormSchema[keyof FormSchema]
                }
              >
                <div className="flex w-full flex-col gap-[1px]">
                  <Tooltip content={fullName}>
                    <span className="truncate text-sm text-gray-700">{fullName}</span>
                  </Tooltip>
                  <span className="truncate text-label-md italic text-gray-500">
                    <AppFormattedMessage id={stakeholderSingularTypeTitle[type]} />
                  </span>
                </div>
              </FormComboboxOption>
            ))}
          </>
        ) : isLoading ? (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            <AppFormattedMessage id="LOADING" />
          </span>
        ) : (
          <span className="py-[6px] text-sm font-normal text-gray-700">
            {emptyListMessage ??
              (hasSearchValue ? (
                <AppFormattedMessage id="NO_SHAREHOLDER_FOUND" />
              ) : (
                <AppFormattedMessage id="NO_RELEVANT_STAKEHOLDERS_FOR_EVENT" />
              ))}
          </span>
        )}
      </InfiniteScroll>

      <Button
        className="w-full justify-start text-nowrap rounded bg-brand-25 px-2 py-[6px] text-start text-sm font-[450] text-brand-700"
        onClick={() => navigate('/stakeholders?openAddModal=true')}
        styleType="NONE"
      >
        {addButtonLabel}
      </Button>
    </div>
  );
};

export type StakeholderComboboxProps = {
  addButtonLabel?: ReactNode;
  companyId: string;
  control: Control<FormSchema>;
  conversionEligible?: boolean;
  emptyListMessage?: ReactNode;
  excludeStakeholderIds?: string[];
  name: string;
  onlyWithShares?: boolean;
  /** Fires only on a user pick, never on hydration — see the reverse-vesting reset callers. */
  onSelectStakeholder?: (stakeholder: StakeholderValue) => void;
  placeholder?: ReactNode;
  stakeholderFromId?: string;
  type?: StakeholderType;
};

export const StakeholderCombobox: FC<StakeholderComboboxProps> = ({
  addButtonLabel,
  companyId,
  control,
  conversionEligible = false,
  emptyListMessage,
  excludeStakeholderIds,
  name,
  onSelectStakeholder,
  onlyWithShares,
  placeholder,
  stakeholderFromId,
  type,
}) => {
  const scrollableTargetId = useId();
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { debounceCallback } = useDebounce<string>({
    callback: (value) => {
      setDebouncedSearch(value);
    },
  });

  const { stakeholders, fetchNextPage, hasNextPage, isLoading } = useInfiniteStakeholders({
    search: debouncedSearch || '',
    companyId,
    sortOrder: StakeholderOrder.ASC,
    type,
  });

  const {
    stakeholders: stakeholdersWithShares,
    fetchNextPage: fetchNextWithShares,
    hasNextPage: hasNextWithShares,
    isLoading: isLoadingWithShares,
  } = useStakeholdersWithShares({
    companyId,
    search: debouncedSearch || '',
    conversionEligible,
    sortOrder: StakeholderOrder.ASC,
  });

  const filteredStakeholders = (() => {
    const base = onlyWithShares ? stakeholdersWithShares : stakeholders;
    if (excludeStakeholderIds?.length) {
      return base.filter(({ id }) => !excludeStakeholderIds.includes(id));
    }
    if (stakeholderFromId) {
      return base.filter(({ id }) => id !== stakeholderFromId);
    }
    return base;
  })();

  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <FormCombobox
        className="relative w-[224px]"
        control={control}
        name={name as keyof FormSchema}
        onFocus={() => {
          setSearch('');
          setDebouncedSearch('');
        }}
        onSelect={(stakeholder) => {
          setSearch('');
          setDebouncedSearch('');
          onSelectStakeholder?.(stakeholder as StakeholderValue);
        }}
      >
        <div className="relative">
          <FormComboboxInput
            className={'bg-gray-900'}
            control={control}
            customValue={(value) => (value as StakeholderValue)?.fullName}
            icon={<ChevronDownIcon className="mt-3" />}
            name={name as keyof FormSchema}
            placeholder={placeholder ?? <AppFormattedMessage id="STAKEHOLDER" />}
            readOnly
          />
          <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
        </div>
        <ComboboxOptions className="w-full overflow-x-hidden" id={scrollableTargetId}>
          <div className="relative flex w-full items-center" onClick={() => ref.current?.focus()}>
            <SearchIcon className="absolute ml-[6px]" />
            <input
              className="w-full rounded border-[2px] border-transparent bg-gray-50 p-[6px] pl-8 text-gray-700 outline-none placeholder:text-gray-400 focus:border-brand-700 focus:bg-white"
              onChange={({ target: { value } }) => {
                setSearch(value);
                debounceCallback(value);
              }}
              ref={ref}
              value={search}
            />
          </div>
          <StakeholdersItemsList
            addButtonLabel={addButtonLabel}
            control={control}
            data={filteredStakeholders || []}
            emptyListMessage={emptyListMessage}
            fetchNextPage={onlyWithShares ? fetchNextWithShares : fetchNextPage}
            hasNextPage={onlyWithShares ? hasNextWithShares : hasNextPage}
            hasSearchValue={!!search?.length}
            isLoading={onlyWithShares ? isLoadingWithShares : isLoading}
            name={name}
            scrollableTargetId={scrollableTargetId}
          />
        </ComboboxOptions>
      </FormCombobox>
    </>
  );
};
